import { useState } from 'react';
import { countLabel, durationLabel, timeAgo, timecode } from '../lib/format';
import { privacyShortLabel } from '../lib/privacy';
import type { VideoSummary } from '../lib/types';
import { waveformEnvelope } from '../lib/waveform';
import { statusLabel } from '../queries/videos';
import { AppLink } from './Nav';
import { CommentIcon, EyeIcon, LockIcon, PlayIcon, ReactionIcon, TrashIcon, WaveformIcon } from './icons';
import { Badge, Button, cx } from './ui';

/**
 * One recording in a library grid. The thumbnail is the poster frame when the
 * transcode has produced one; before that it is a slate (literally, the
 * clapper stripes) with the status on it, so a processing video still reads
 * as a real object rather than a hole in the grid.
 *
 * An audio note never gets a poster, because there is no frame to grab. Rather
 * than leave a permanent hole, it draws its own mark: a seeded waveform that
 * is the same every time you look at it, so a mixed library is scannable at a
 * glance instead of being a wall of identical grey rectangles.
 */

const CARD_BARS = 28;

function AudioThumbnail({ video }: { video: VideoSummary }) {
  const envelope = waveformEnvelope(video.shareId, CARD_BARS);

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-900">
      <div className="flex h-12 w-3/4 items-center justify-center gap-[3px]" aria-hidden="true">
        {envelope.map((height, index) => (
          <span
            key={index}
            className="w-full max-w-[5px] flex-1 rounded-full bg-slate-700 transition-colors duration-120 group-hover:bg-slate-600"
            style={{ height: `${height * 100}%`, minHeight: '3px' }}
          />
        ))}
      </div>
      <span className="flex items-center gap-1.5 text-micro uppercase tracking-[0.08em] text-chalk-faint">
        <WaveformIcon size={12} />
        Audio note
      </span>
    </div>
  );
}

function Thumbnail({ video }: { video: VideoSummary }) {
  const ready = video.status === 'ready';

  if (video.kind === 'audio') {
    return (
      <div className="relative aspect-video w-full overflow-hidden rounded-t-md bg-slate-900">
        {ready ? (
          <AudioThumbnail video={video} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="clapper-stripes absolute inset-x-0 top-0 h-2 opacity-[0.10]" />
            <span className="slate-type text-caption text-chalk-faint">
              {video.status === 'failed' ? 'Failed' : 'Processing'}
            </span>
          </div>
        )}

        {video.durationSec ? (
          <span
            data-timecode
            className="absolute bottom-2 right-2 rounded-xs bg-slate-950/85 px-1.5 py-0.5 font-mono text-micro text-chalk"
          >
            {durationLabel(video.durationSec)}
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-t-md bg-slate-900">
      {video.posterUrl ? (
        <img
          src={video.posterUrl}
          alt=""
          loading="lazy"
          className={cx(
            'absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]',
            !ready && 'opacity-60',
          )}
        />
      ) : null}
      {video.locked ? (
        // Past the free plan's window. The frame stays, dimmed, with a lock:
        // the recording exists, it is the plan that is in the way.
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-slate-950/60">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-950/80 text-chalk">
            <LockIcon size={18} />
          </span>
          <span className="text-micro uppercase tracking-[0.08em] text-chalk-dim">Locked</span>
        </div>
      ) : ready ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-950/70 text-chalk opacity-0 transition-opacity duration-120 group-hover:opacity-100">
            <PlayIcon size={18} />
          </span>
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="clapper-stripes absolute inset-x-0 top-0 h-2 opacity-[0.10]" />
          <span className="slate-type rounded-xs bg-slate-950/70 px-2 py-0.5 text-caption text-chalk-faint">
            {video.status === 'failed' ? 'Failed' : 'Processing'}
          </span>
        </div>
      )}

      {video.durationSec ? (
        <span
          data-timecode
          className="absolute bottom-2 right-2 rounded-xs bg-slate-950/85 px-1.5 py-0.5 font-mono text-micro text-chalk"
        >
          {durationLabel(video.durationSec)}
        </span>
      ) : null}
    </div>
  );
}

export function VideoCard({
  video,
  onRestore,
  onDelete,
  onTag,
  deleting = false,
}: {
  video: VideoSummary;
  /** Present where a tag can be a filter: the library. */
  onTag?: (tagId: string) => void;
  /** Present in the archive: brings it back. */
  onRestore?: (video: VideoSummary) => void;
  /**
   * Present on a live recording the person may manage: sends it to the
   * archive. Deleting is two presses, never one, and the second one is the
   * red button, so nothing goes anywhere on a stray click.
   */
  onDelete?: (video: VideoSummary) => void;
  /** This card's delete is in flight. */
  deleting?: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const reactionCount = video._count.reactions ?? 0;
  const ownerName = video.owner?.name?.trim() || 'You';
  const initial = ownerName.charAt(0).toUpperCase();
  const archived = Boolean(video.deletedAt);
  const daysLeft = video.deletedAt
    ? Math.max(0, 30 - Math.floor((Date.now() - new Date(video.deletedAt).getTime()) / 86_400_000))
    : null;

  return (
    <article className="group card overflow-hidden transition-colors duration-120 hover:border-slate-600">
      {archived ? (
        <div className="opacity-70">
          <Thumbnail video={video} />
        </div>
      ) : (
        <AppLink to="/v/$shareId" params={{ shareId: video.shareId }} className="block">
          <Thumbnail video={video} />
        </AppLink>
      )}

      <div className="space-y-2 p-3.5">
        <div className="flex items-center gap-2.5">
          {video.owner?.avatarUrl ? (
            <img src={video.owner.avatarUrl} alt="" referrerPolicy="no-referrer" className="h-8 w-8 shrink-0 rounded-full object-cover" />
          ) : (
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-700 text-caption font-medium text-chalk">
              {initial}
            </span>
          )}
          <div className="min-w-0 leading-tight">
            <p className="truncate text-caption text-chalk">
              {ownerName} <span className="text-chalk-faint">· {timeAgo(video.createdAt)}</span>
            </p>
            {archived ? (
              <p className="text-caption text-chalk-faint">
                {daysLeft === 0 ? 'Gone with the next sweep' : `Gone in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`}
              </p>
            ) : (
              <p className="text-caption text-chalk-faint">{privacyShortLabel(video.visibility)}</p>
            )}
          </div>
          {video.status !== 'ready' && !archived ? (
            <span className="ml-auto shrink-0">
              <Badge tone={video.status === 'failed' ? 'danger' : 'caution'}>{statusLabel(video.status)}</Badge>
            </span>
          ) : null}
        </div>

        {archived ? (
          <p className="line-clamp-2 min-h-[2.5rem] text-body font-medium text-chalk">{video.title}</p>
        ) : (
          <AppLink
            to="/v/$shareId"
            params={{ shareId: video.shareId }}
            className="line-clamp-2 min-h-[2.5rem] text-body font-medium text-chalk hover:underline"
          >
            {video.title}
          </AppLink>
        )}

        {video.tags && video.tags.length > 0 ? (
          <ul className="flex flex-wrap gap-1" aria-label="Tags">
            {video.tags.map((tag) => (
              <li key={tag.id}>
                <button
                  type="button"
                  disabled={!onTag}
                  onClick={() => onTag?.(tag.id)}
                  title={tag.scope === 'personal' ? 'Only you see this tag' : undefined}
                  className={cx(
                    'rounded-full border bg-slate-900 px-2 py-0.5 text-micro text-chalk-dim transition-colors duration-120',
                    tag.scope === 'personal' ? 'border-dashed border-chalk/30' : 'border-hairline',
                    onTag ? 'hover:border-slate-600 hover:text-chalk' : 'cursor-default',
                  )}
                >
                  {tag.name}
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="flex items-center gap-4 text-caption text-chalk-faint">
          <span className="flex items-center gap-1 tnum" title={countLabel(video._count.views, 'view')}>
            <EyeIcon size={14} />
            {video._count.views}
          </span>
          <span className="flex items-center gap-1 tnum" title={countLabel(video._count.comments, 'comment')}>
            <CommentIcon size={14} />
            {video._count.comments}
          </span>
          <span className="flex items-center gap-1 tnum" title={countLabel(reactionCount, 'reaction')}>
            <ReactionIcon size={14} />
            {reactionCount}
          </span>
          {video.kind === 'audio' ? <Badge tone="neutral">Audio</Badge> : null}
          {video.visibility !== 'public' ? <Badge>{privacyShortLabel(video.visibility)}</Badge> : null}
          <span className="flex-1" />
          {archived && onRestore ? (
            <Button size="sm" onClick={() => onRestore(video)}>
              Restore
            </Button>
          ) : null}
          {!archived && onDelete ? (
            confirming ? (
              <span className="flex items-center gap-1.5">
                <Button variant="danger" size="sm" busy={deleting} onClick={() => onDelete(video)}>
                  Delete
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                  Keep
                </Button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setConfirming(true)}
                aria-label={`Delete ${video.title}`}
                title="Send to the archive"
                className="flex h-7 w-7 items-center justify-center rounded text-chalk-faint transition-colors duration-120 hover:bg-slate-800 hover:text-chalk"
              >
                <TrashIcon size={14} />
              </button>
            )
          ) : null}
        </div>

        {!archived && onDelete && confirming ? (
          <p className="text-caption text-chalk-faint">
            It goes to the archive, and the link stops working. You have thirty days to bring it
            back.
          </p>
        ) : null}
      </div>
    </article>
  );
}

export function VideoGrid({
  videos,
  className,
  onRestore,
  onDelete,
  onTag,
  canDelete,
  deletingId = null,
}: {
  videos: VideoSummary[];
  className?: string;
  onRestore?: (video: VideoSummary) => void;
  onDelete?: (video: VideoSummary) => void;
  onTag?: (tagId: string) => void;
  /** Which of these the person may actually delete. Without it, all of them. */
  canDelete?: (video: VideoSummary) => boolean;
  /** The recording whose delete is in flight. */
  deletingId?: string | null;
}) {
  return (
    <div className={cx('grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3', className)}>
      {videos.map((video) => (
        <VideoCard
          key={video.id}
          video={video}
          onRestore={onRestore}
          onDelete={onDelete && (!canDelete || canDelete(video)) ? onDelete : undefined}
          onTag={onTag}
          deleting={deletingId === video.id}
        />
      ))}
    </div>
  );
}

export function VideoGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="card overflow-hidden">
          <div className="shimmer aspect-video w-full" />
          <div className="space-y-2 p-3.5">
            <div className="shimmer h-3.5 w-3/4 rounded-xs" />
            <div className="shimmer h-3 w-1/3 rounded-xs" />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Used in list contexts where a grid is too heavy (org admin views). */
export function VideoRow({ video }: { video: VideoSummary }) {
  return (
    <div className="flex items-center gap-3 border-b border-hairline py-3 last:border-b-0">
      <span
        className="shrink-0 text-chalk-faint"
        title={video.kind === 'audio' ? 'Audio note' : 'Screen recording'}
      >
        {video.kind === 'audio' ? <WaveformIcon size={14} /> : <PlayIcon size={14} />}
      </span>
      <div className="min-w-0 flex-1">
        <AppLink
          to="/v/$shareId"
          params={{ shareId: video.shareId }}
          className="block truncate text-body text-chalk hover:underline"
        >
          {video.title}
        </AppLink>
        <p className="text-caption text-chalk-faint">
          {timeAgo(video.createdAt)} · {countLabel(video._count.views, 'view')}
        </p>
      </div>
      <span data-timecode className="font-mono text-caption text-chalk-faint">
        {timecode(video.durationSec)}
      </span>
      {video.status !== 'ready' ? (
        <Badge tone={video.status === 'failed' ? 'danger' : 'caution'}>
          {statusLabel(video.status)}
        </Badge>
      ) : null}
    </div>
  );
}
